import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../../hooks/useLanguages';
import { loadPhrasebookAll } from '../../services/basicPhrasesV2';
import LanguageFlag from '../../components/LanguageFlag';
import { colorForCategoryIndex, emojiForCategoryIndex, speakPhrase } from './phrasebookShared';

const PRIMARY_CATEGORY_COUNT = 4;
const PREVIEW_CANDIDATES = ['en', 'es', 'ja', 'hi', 'fr', 'zh'];
const PREVIEW_COUNT = 3;
const QUICK_BROWSE_CODES = ['en', 'es', 'fr', 'de', 'zh', 'ja', 'ko'];

export default function PhrasebookPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { languages } = useLanguages();
  const anchorCode = (i18n.language || 'en').split('-')[0];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [translationsByKey, setTranslationsByKey] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showMoreCategories, setShowMoreCategories] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    loadPhrasebookAll()
      .then((all) => {
        if (!active) return;
        const byKey = {};
        const nextEntries = [];
        const seenCategories = [];
        Object.entries(all).forEach(([key, value]) => {
          const categoryByLang = value.category || {};
          const textByLang = value.text || {};
          byKey[key] = { ...textByLang };

          const category = categoryByLang[anchorCode] ?? categoryByLang.en;
          const text = textByLang[anchorCode] ?? textByLang.en;
          if (category == null || text == null) return;

          let categoryIndex = seenCategories.indexOf(category);
          if (categoryIndex < 0) {
            seenCategories.push(category);
            categoryIndex = seenCategories.length - 1;
          }
          nextEntries.push({ key, category, categoryIndex, text });
        });
        setEntries(nextEntries);
        setCategories(seenCategories);
        setTranslationsByKey(byKey);
        setLoading(false);
      })
      .catch(() => {
        if (active) { setError(true); setLoading(false); }
      });
    return () => { active = false; };
  }, [anchorCode]);

  const langByCode = useMemo(
    () => Object.fromEntries(languages.map((l) => [l.code, l])),
    [languages],
  );

  const filtered = selectedCategory == null
    ? entries
    : entries.filter((e) => e.category === selectedCategory);

  const primaryCategories = categories.slice(0, PRIMARY_CATEGORY_COUNT);
  const restCategories = categories.slice(PRIMARY_CATEGORY_COUNT);

  const quickLanguages = QUICK_BROWSE_CODES.map((c) => langByCode[c]).filter(Boolean);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${t('phrasebookPage.title')} | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [t]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-4xl">
        <header className="mb-6">
          <h1 className="text-2xl font-black text-slate-900 sm:text-3xl">{t('phrasebookPage.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('phrasebookPage.subtitle')}</p>
        </header>

        {loading && (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((item) => <div key={item} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🌐</span>
            <p className="mt-3 text-sm font-semibold text-slate-600">{t('phrasebookPage.unavailable')}</p>
            <button
              type="button"
              onClick={() => { setLoading(true); setError(false); loadPhrasebookAll({ force: true }); }}
              className="mt-4 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700"
            >
              {t('phrasebookPage.tryAgain')}
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Category filter chips */}
            <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
              <div className="flex gap-2">
                <CategoryChip
                  emoji="🗂️"
                  label={t('phrasebookPage.allCategories')}
                  color="#4F46E5"
                  selected={selectedCategory == null}
                  onClick={() => setSelectedCategory(null)}
                />
                {primaryCategories.map((category, index) => (
                  <CategoryChip
                    key={category}
                    emoji={emojiForCategoryIndex(index)}
                    label={category}
                    color={colorForCategoryIndex(index)}
                    selected={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  />
                ))}
                {restCategories.length > 0 && (
                  <CategoryChip
                    emoji={showMoreCategories ? '▲' : '▼'}
                    label={t('phrasebookPage.more')}
                    color="#64748B"
                    selected={false}
                    onClick={() => setShowMoreCategories((v) => !v)}
                  />
                )}
              </div>
            </div>
            {showMoreCategories && restCategories.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {restCategories.map((category, index) => (
                  <CategoryChip
                    key={category}
                    emoji={emojiForCategoryIndex(index + PRIMARY_CATEGORY_COUNT)}
                    label={category}
                    color={colorForCategoryIndex(index + PRIMARY_CATEGORY_COUNT)}
                    selected={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  />
                ))}
              </div>
            )}

            {/* Phrase list */}
            <div className="mt-5 space-y-3">
              {filtered.length === 0 && (
                <p className="py-8 text-center text-sm text-slate-500">{t('phrasebookPage.noResults')}</p>
              )}
              {filtered.map((entry) => {
                const translations = translationsByKey[entry.key] || {};
                const previewCodes = PREVIEW_CANDIDATES.filter(
                  (c) => c !== anchorCode && translations[c] != null,
                ).slice(0, PREVIEW_COUNT);
                const hiddenCount = Math.max(
                  0,
                  Object.keys(translations).length - previewCodes.length - 1,
                );
                const anchorLang = langByCode[anchorCode];
                const color = colorForCategoryIndex(entry.categoryIndex);
                return (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => navigate(`/phrasebook/phrase/${encodeURIComponent(entry.key)}`)}
                    className="flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 text-left shadow-sm transition hover:shadow-md"
                  >
                    <div className="w-24 shrink-0">
                      <span
                        className="grid h-9 w-9 place-items-center rounded-xl text-base"
                        style={{ backgroundColor: `${color}1f` }}
                      >
                        {emojiForCategoryIndex(entry.categoryIndex)}
                      </span>
                      <p className="mt-1.5 line-clamp-2 text-sm font-extrabold text-slate-900">{entry.text}</p>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); speakPhrase(anchorLang?.tts || 'en-US', entry.text); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); speakPhrase(anchorLang?.tts || 'en-US', entry.text); } }}
                        className="mt-0.5 inline-block text-indigo-600"
                        aria-label={t('phrasebookPage.listen')}
                      >
                        🔊
                      </span>
                    </div>
                    <div
                      className="flex min-w-0 flex-1 gap-2 overflow-x-auto"
                      style={{ maskImage: 'linear-gradient(to right, black 85%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, black 85%, transparent 100%)' }}
                    >
                      {previewCodes.map((code) => {
                        const lang = langByCode[code];
                        return (
                          <div key={code} className="w-24 shrink-0 rounded-xl border border-slate-100 bg-slate-50 p-2">
                            <LanguageFlag flag={lang?.flag} label={lang?.label} className="h-3.5 w-5" />
                            <p dir={lang?.dir || 'ltr'} className="mt-1 line-clamp-2 text-xs font-semibold text-slate-700">
                              {translations[code]}
                            </p>
                            <p className="truncate text-[10px] text-slate-400">{lang?.nativeLabel || code}</p>
                          </div>
                        );
                      })}
                      {hiddenCount > 0 && (
                        <div className="grid w-16 shrink-0 place-items-center rounded-xl border border-dashed border-slate-200 text-xs font-bold text-slate-400">
                          +{hiddenCount}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Browse by language */}
            <section className="mt-10">
              <h2 className="text-xs font-black uppercase tracking-wide text-slate-500">{t('phrasebookPage.browseLanguages')}</h2>
              <div className="mt-3 flex flex-wrap gap-4">
                {quickLanguages.map((lang) => (
                  <LanguageAvatar
                    key={lang.code}
                    lang={lang}
                    onClick={() => navigate(`/phrasebook/language/${lang.code}`)}
                  />
                ))}
                <button type="button" onClick={() => setPickerOpen(true)} className="flex w-16 flex-col items-center gap-1">
                  <span className="grid h-12 w-12 place-items-center rounded-full border border-slate-200 bg-white text-slate-500">⋯</span>
                  <span className="truncate text-[11px] text-slate-500">{t('phrasebookPage.more')}</span>
                </button>
              </div>
            </section>
          </>
        )}
      </main>

      {pickerOpen && (
        <LanguagePicker
          languages={languages}
          title={t('phrasebookPage.pickLanguage')}
          onClose={() => setPickerOpen(false)}
          onSelect={(code) => { setPickerOpen(false); navigate(`/phrasebook/language/${code}`); }}
        />
      )}
    </div>
  );
}

function CategoryChip({ emoji, label, color, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3.5 text-sm font-bold transition"
      style={selected
        ? { backgroundColor: color, borderColor: color, color: '#fff' }
        : { backgroundColor: '#fff', borderColor: '#e2e8f0', color: '#0f172a' }}
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-full text-xs"
        style={{ backgroundColor: selected ? 'rgba(255,255,255,0.22)' : `${color}1f` }}
      >
        {emoji}
      </span>
      {label}
    </button>
  );
}

function LanguageAvatar({ lang, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex w-16 flex-col items-center gap-1">
      <span className="grid h-12 w-12 place-items-center rounded-full border border-slate-200 bg-white text-xl">
        <LanguageFlag flag={lang.flag} label={lang.label} className="h-5 w-7" />
      </span>
      <span className="truncate text-[11px] text-slate-500">{lang.nativeLabel || lang.label}</span>
    </button>
  );
}

function LanguagePicker({ languages, title, onClose, onSelect }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[80vh] w-full max-w-sm flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <p className="text-sm font-black text-slate-900">{title}</p>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => onSelect(lang.code)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              <LanguageFlag flag={lang.flag} label={lang.label} className="h-4 w-6" />
              {lang.nativeLabel || lang.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
